import axios, { AxiosError } from "axios";
import { BASE_URL } from "./constants";
import { contract } from "../api/generated";
import type z from "zod";

const DEFAULT_API_CONFIG = {
  url: BASE_URL,
  timeout: 10000,
};

/**
 * Logs the error and rejects the promise with the HTTP problems based error body.
 * @param {import("axios").AxiosError} error
 * @returns {Promise<{*} | undefined>}
 */
function logErrorAndRejectBody(error: AxiosError) {
  console.error("API Error:", error);
  return Promise.reject(error?.response?.data);
}

/**
 * Response-interceptor error handler: when a previously-valid session is
 * rejected with 401 (token expired, account deleted, or a global sign-out),
 * clear the stored token and send the user to the login page. A 403 (a valid
 * session that merely lacks permission) is intentionally NOT treated as a
 * logout. Always re-rejects so callers still see the error.
 */
export function clearSessionOnUnauthorized(error: AxiosError): Promise<never> {
  const status = error?.response?.status;
  const hadSession =
    typeof window !== "undefined" &&
    window.sessionStorage.getItem("token") != null;
  if (status === 401 && hadSession) {
    window.sessionStorage.removeItem("token");
    if (window.location.pathname !== "/") {
      window.location.assign("/");
    }
  }
  return Promise.reject(error);
}

export type UsersSortOptions = z.infer<
  typeof contract.getAllUsers.query.shape.sort
>;

export type FilesSortOptions = z.infer<
  typeof contract.apiFiles.query.shape.sort
>;

export type RegisterUserRequest = z.infer<typeof contract.registerUser.body>;

export type AppVersionDTO = {
  id: string;
  _id: string;
  versionName: string;
  versionCode: number;
  isDebug: boolean;
  packageName: string;
  status: "DRAFT" | "ARCHIVED" | "PUBLISHED";
  forceUpdate: boolean;
  downloadUrl: string;
  releaseNotes: string;
  sha256: string;
  createdAt: string;
};

export type AppVersionPatch = Partial<
  Pick<
    AppVersionDTO,
    | "versionName"
    | "versionCode"
    | "packageName"
    | "status"
    | "forceUpdate"
    | "downloadUrl"
    | "releaseNotes"
    | "sha256"
  >
>;

export type AppVersionCreate = Pick<
  AppVersionDTO,
  | "versionName"
  | "versionCode"
  | "packageName"
  | "status"
  | "downloadUrl"
  | "releaseNotes"
  | "sha256"
> &
  Partial<Pick<AppVersionDTO, "forceUpdate" | "isDebug">>;

export type AppVersionsSortOptions =
  | "versionName_asc"
  | "versionName_desc"
  | "versionCode_asc"
  | "versionCode_desc"
  | "status_asc"
  | "status_desc"
  | "forceUpdate_asc"
  | "forceUpdate_desc"
  | "createdAt_asc"
  | "createdAt_desc";

export type FirmwareVersionDTO = {
  id: string;
  _id: string;
  versionName: string;
  versionCode: number;
  fileName: string;
  fileSize: number;
  forceUpdate: boolean;
  status: "DRAFT" | "ARCHIVED" | "PUBLISHED";
  downloadUrl: string;
  releaseNotes: string;
  sha256: string;
  createdAt: string;
};

export type FirmwareVersionPatch = Partial<
  Pick<
    FirmwareVersionDTO,
    | "versionName"
    | "versionCode"
    | "fileName"
    | "fileSize"
    | "forceUpdate"
    | "status"
    | "downloadUrl"
    | "releaseNotes"
    | "sha256"
  >
>;

export type FirmwareVersionCreate = Pick<
  FirmwareVersionDTO,
  | "versionName"
  | "fileName"
  | "fileSize"
  | "forceUpdate"
  | "status"
  | "downloadUrl"
  | "releaseNotes"
  | "sha256"
> &
  Partial<Pick<FirmwareVersionDTO, "versionCode">>;

export type FirmwareVersionsSortOptions =
  | "versionName_asc"
  | "versionName_desc"
  | "versionCode_asc"
  | "versionCode_desc"
  | "status_asc"
  | "status_desc"
  | "fileName_asc"
  | "fileName_desc"
  | "fileSize_asc"
  | "fileSize_desc"
  | "forceUpdate_asc"
  | "forceUpdate_desc"
  | "createdAt_asc"
  | "createdAt_desc";

export class Api {
  axios;
  config;

  constructor(config = DEFAULT_API_CONFIG) {
    this.config = config;

    this.axios = axios.create({
      baseURL: this.config.url,
      timeout: this.config.timeout,
      headers: {
        "content-type": "application/json",
        Accept: "application/json",
        "Access-Control-Allow-Origin": "*",
      },
    });

    this.axios.interceptors.request.use((config) => {
      if (typeof FormData !== "undefined" && config.data instanceof FormData) {
        config.headers.setContentType(false);
      }
      return config;
    });

    // Clear the session and redirect to login when a previously-valid session
    // is rejected (see clearSessionOnUnauthorized).
    this.axios.interceptors.response.use(
      (response) => response,
      clearSessionOnUnauthorized
    );
  }

  /**
   * Admin-only: invalidate every active session (all users must sign in again).
   */
  signOutAllSessions = async (token: string) => {
    return this.axios
      .post("/api/sessions/sign-out-all", undefined, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => response.data)
      .then((data) => contract.signOutAllSessions.responses[200].parse(data))
      .catch(logErrorAndRejectBody);
  };

  /**
   * Create a customer user as an authenticated Admin.
   */
  registerUser = async (token: string, user: RegisterUserRequest) => {
    return this.axios
      .post("/api/users", user, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => response.data)
      .then((data) => contract.registerUser.responses[200].parse(data))
      .catch(logErrorAndRejectBody);
  };

  /**
   * Get users matching criteria
   * @param token The user token
   * @returns users
   */
  getUsers = async (
    token: string,
    limit: number,
    offset?: number,
    sort?: UsersSortOptions,
    search?: string,
    fileName?: string
  ) => {
    return this.axios
      .get("/api/users", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: { limit, offset, sort, search, filename: fileName },
      })
      .then((response) => response.data)
      .then((data) => contract.getAllUsers.responses[200].parse(data))
      .catch(logErrorAndRejectBody);
  };

  async getUsersByFileAssignment(token: string, fileName: string) {
    const users = await this.getUsers(
      token,
      1000,
      undefined,
      undefined,
      undefined,
      fileName
    ).then((response) => response.data);
    return users.filter((user) => {
      return user.files && user.files.some((file) => file === fileName);
    });
  }

  /**
   * Get files matching criteria
   * @param token The user token
   * @param limit The maximum number of files to return at a time
   * @param offset The number of files to skip
   * @param sort The sort order
   * @param search The search text used to filter files
   * @returns files
   */
  getFiles = async (
    token: string,
    limit: number,
    offset?: number,
    sort?: FilesSortOptions,
    search?: string
  ) => {
    // let url = ;
    // if (search) {
    //   url += `?search=${search}`;
    // }

    return this.axios
      .get("/api/files", {
        headers: { Authorization: `Bearer ${token}` },
        params: { limit, offset, sort, search },
      })
      .then((response) => response.data)
      .then((data) => contract.apiFiles.responses[200].parse(data))
      .catch(logErrorAndRejectBody);
  };

  /**
   * Get app versions matching criteria
   * @param token The user token
   * @param limit The maximum number of versions to return at a time
   * @param offset The number of versions to skip
   * @param sort The sort order
   * @param search The search text used to filter versions
   */
  getAppVersions = async (
    token: string,
    limit: number,
    offset?: number,
    sort?: AppVersionsSortOptions,
    search?: string,
    filters?: {
      isDebug?: boolean;
      status?: AppVersionDTO["status"];
    }
  ) => {
    return this.axios
      .get("/api/updates/versions", {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          limit,
          offset,
          sort,
          search,
          ...(filters?.isDebug !== undefined
            ? { isDebug: String(filters.isDebug) }
            : {}),
          ...(filters?.status ? { status: filters.status } : {}),
        },
      })
      .then(
        (response) =>
          response.data as {
            status: string;
            metadata: { totalCount: number };
            data: AppVersionDTO[];
          }
      )
      .catch(logErrorAndRejectBody);
  };

  /**
   * Create app version
   */
  createAppVersion = async (token: string, payload: AppVersionCreate) => {
    return this.axios
      .post("/api/updates/versions", payload, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(
        (response) => response.data as { status: string; data: AppVersionDTO }
      )
      .catch(logErrorAndRejectBody);
  };

  /**
   * Upload APK file for app versions
   */
  uploadAppApk = async (token: string, formData: FormData) => {
    return this.axios
      .post("/api/updates/apk-upload", formData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(
        (response) =>
          response.data as {
            status: string;
            message: string;
            data: {
              fileName: string;
              downloadUrl: string;
              sha256: string;
              packageName: string;
              versionName: string;
              versionCode: number;
              isDebug: boolean;
            };
          }
      )
      .catch(logErrorAndRejectBody);
  };

  /**
   * Update app version by ID
   */
  updateAppVersion = async (
    token: string,
    versionId: string,
    patch: AppVersionPatch
  ) => {
    return this.axios
      .patch(`/api/updates/versions/${versionId}`, patch, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(
        (response) => response.data as { status: string; data: AppVersionDTO }
      )
      .catch(logErrorAndRejectBody);
  };

  /**
   * Delete app version by ID
   */
  deleteAppVersion = async (token: string, versionId: string) => {
    return this.axios
      .delete(`/api/updates/versions/${versionId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => response.data as { status: string; data: "Deleted" })
      .catch(logErrorAndRejectBody);
  };

  /**
   * Get firmware versions matching criteria
   */
  getFirmwareVersions = async (
    token: string,
    limit: number,
    offset?: number,
    sort?: FirmwareVersionsSortOptions,
    search?: string
  ) => {
    return this.axios
      .get("/api/updates/firmware-versions", {
        headers: { Authorization: `Bearer ${token}` },
        params: { limit, offset, sort, search },
      })
      .then(
        (response) =>
          response.data as {
            status: string;
            metadata: { totalCount: number };
            data: FirmwareVersionDTO[];
          }
      )
      .catch(logErrorAndRejectBody);
  };

  /**
   * Create firmware version with file upload in one request
   */
  createFirmwareVersionWithFile = async (token: string, formData: FormData) => {
    return this.axios
      .post("/api/updates/firmware-versions/with-file", formData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        timeout: 120000,
      })
      .then(
        (response) =>
          response.data as { status: string; data: FirmwareVersionDTO }
      )
      .catch(logErrorAndRejectBody);
  };

  getSuggestedFirmwareVersionCode = async (token: string) => {
    return this.axios
      .get("/api/updates/firmware-versions/suggested-version-code", {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(
        (response) =>
          response.data as { status: string; data: { versionCode: number } }
      )
      .catch(logErrorAndRejectBody);
  };

  /**
   * Create firmware version
   */
  createFirmwareVersion = async (
    token: string,
    payload: FirmwareVersionCreate
  ) => {
    return this.axios
      .post("/api/updates/firmware-versions", payload, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(
        (response) =>
          response.data as { status: string; data: FirmwareVersionDTO }
      )
      .catch(logErrorAndRejectBody);
  };

  /**
   * Upload firmware BIN file
   */
  uploadFirmware = async (token: string, formData: FormData) => {
    return this.axios
      .post("/api/updates/firmware-upload", formData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(
        (response) =>
          response.data as {
            status: string;
            message: string;
            data: {
              fileName: string;
              downloadUrl: string;
              sha256: string;
              fileSize: number;
              versionName: string;
              suggestedVersionName: string;
            };
          }
      )
      .catch(logErrorAndRejectBody);
  };

  /**
   * Update firmware version by ID
   */
  updateFirmwareVersion = async (
    token: string,
    versionId: string,
    patch: FirmwareVersionPatch
  ) => {
    return this.axios
      .patch(`/api/updates/firmware-versions/${versionId}`, patch, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(
        (response) =>
          response.data as { status: string; data: FirmwareVersionDTO }
      )
      .catch(logErrorAndRejectBody);
  };

  /**
   * Delete firmware version by ID
   */
  deleteFirmwareVersion = async (token: string, versionId: string) => {
    return this.axios
      .delete(`/api/updates/firmware-versions/${versionId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => response.data as { status: string; data: "Deleted" })
      .catch(logErrorAndRejectBody);
  };

  publishFirmwareVersion = async (token: string, versionId: string) => {
    return this.axios
      .post(
        `/api/updates/firmware-versions/${versionId}/publish`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      )
      .then(
        (response) =>
          response.data as { status: string; data: FirmwareVersionDTO }
      )
      .catch(logErrorAndRejectBody);
  };

  /**
   * Download firmware file
   * @param token The user token
   * @param fileName The firmware file name
   * @returns firmware blob
   */
  downloadFirmware = async (token: string, fileName: string) => {
    return this.axios
      .get(`/api/updates/firmware/${encodeURIComponent(fileName)}`, {
        responseType: "blob",
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => response.data)
      .catch(logErrorAndRejectBody);
  };

  /**
   * Get files assigned to a user
   * @param token The user token
   * @param userId The user id
   * @param searchText The Search Term
   * @returns files
   */
  getFilesAssignedToUser = async (
    token: string,
    userId?: string,
    searchText?: string
  ) => {
    if (!userId) return { files: [] };
    let url = `/api/users/${userId}/files`;
    if (searchText) {
      url += `?search=${searchText}`;
    }

    return this.axios
      .get(url, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => response.data)
      .then((data) => contract.apiUsersUserIdFiles.responses[200].parse(data))
      .catch(logErrorAndRejectBody);
  };

  /**
   * Assign file to a user
   * @param token The user token
   * @param postData The data to post
   * @returns postData {userId; fileId}
   */
  assignFileToUser = async (
    token: string,
    postData: { userId: string; fileId: string }
  ) => {
    return this.axios
      .post("/api/assign-file", postData, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => response.data)
      .then((data) => contract.apiAssignFile.responses[200].parse(data))
      .catch(logErrorAndRejectBody);
  };

  /**
   * Unassign file from a user
   * @param token The user token
   * @param fileId The file id
   * @param userId The user id
   * @returns status
   */
  unassignFileFromUser = async (
    token: string,
    fileId: string,
    userId: string
  ) => {
    return this.axios
      .delete(`/api/users/${userId}/files/${fileId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => response.data)
      .then((data) => contract.unassignFile.responses[200].parse(data))
      .catch(logErrorAndRejectBody);
  };

  /**
   * Login a user
   * @param data Login Data
   */
  login = async (data: { email: string; password: string }) => {
    return this.axios
      .post("/api/login-user", data)
      .then((response) => response.data)
      .then((data) => contract.apiLoginUser.responses[201].parse(data))
      .catch(logErrorAndRejectBody);
  };

  /**
   * Get user files
   * @param token The user token
   * @param userId The user id
   */
  getUserFiles = async (token: string, userId: string) => {
    return axios
      .get(`/api/users/${userId}/files`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => response.data)
      .then((data) => contract.apiUsersUserIdFiles.responses[200].parse(data))
      .catch(logErrorAndRejectBody);
  };

  /**
   * Delete a user
   * @param token The user token
   * @param userId The user id
   */
  deleteUser = async (token: string, userId: string) => {
    return this.axios
      .delete("/api/users/" + userId, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => response.data)
      .then((data) => contract.deleteUserByUserId.responses[200].parse(data))
      .catch(logErrorAndRejectBody);
  };

  /**
   * Promote a customer user to an administrator.
   */
  promoteUser = async (token: string, userId: string) => {
    return this.axios
      .put(`/api/users/${userId}/roles/admin`, undefined, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => response.data)
      .then((data) => contract.promoteUserToAdmin.responses[200].parse(data))
      .catch(logErrorAndRejectBody);
  };

  /**
   * Demote an administrator back to a regular user. The backend increments the
   * target's session version, ending their existing sessions on next request.
   */
  demoteUser = async (token: string, userId: string) => {
    return this.axios
      .delete(`/api/users/${userId}/roles/admin`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => response.data)
      .then((data) => contract.demoteAdminToUser.responses[200].parse(data))
      .catch(logErrorAndRejectBody);
  };

  /**
   * Download a File
   * @param token The user token
   * @param fileName The file name
   */
  downloadFile = async (token: string, fileName: string) => {
    return (
      this.axios
        .get("/api/file-download/" + fileName, {
          responseType: "blob",
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((response) => response.data)
        // .then((data) =>
        //   contract.apiFileDownloadFileName.responses[200].parse(data)
        // )
        .catch(logErrorAndRejectBody)
    );
  };

  /**
   * Download User File
   * @param userId The file name
   */
  downloadUserFile = async (token: string, userId: string) => {
    return (
      this.axios
        .get("/api/file-download/user/" + userId, {
          responseType: "blob",
          headers: { Authorization: `Bearer ${token}` },
        })
        // .then((data) =>
        //   contract.apiFileDownloadUserUserId.responses[200].parse(data)
        // )
        .catch(logErrorAndRejectBody)
    );
  };

  /**
   * Upload a file
   * @param token The user token
   * @param formData The file
   */
  uploadFile = async (token: string, formData: FormData) => {
    return this.axios
      .post("/api/file-upload/", formData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => response.data)
      .then((data) => contract.apiFileUpload.responses[200].parse(data))
      .catch(logErrorAndRejectBody);
  };

  /**
   * Delete a user
   * @param token The user token
   * @param userId The user id
   */
  deleteFile = async (token: string, fileId: string) => {
    return this.axios
      .delete("/api/files/" + fileId, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => response.data)
      .then((data) => contract.deleteFileByFileId.responses[200].parse(data))
      .catch(logErrorAndRejectBody);
  };

  /**
   * Update a file's metadata
   * @param token The user token
   * @param fileId The file id
   * @param file The file data (only fileName is updatable)
   */
  updateFile = async (
    token: string,
    fileId: string,
    file: { fileName: string }
  ) => {
    return this.axios
      .patch("/api/files/" + fileId, file, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => response.data)
      .then((data) => contract.updateFileByFileId.responses[200].parse(data))
      .catch(logErrorAndRejectBody);
  };
}

export const api = new Api();
